'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AppPhoneInput from '@/hotdesk-ui/components/shared/AppPhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import Image from 'next/image';
import { User, Mail, Building2, Users, Globe, Check } from 'lucide-react';
import { LanguageCode } from '@/hotdesk-ui/constants/langulage.constant';
import { commonConstant } from '@/hotdesk-ui/constants/common.constant';
import SelectMenu, { type SelectMenuOption } from '@/hotdesk-ui/components/shared/SelectMenu';
import SearchSelect from '@/hotdesk-ui/components/shared/SearchSelect';
import { searchCountries } from '@/hotdesk-ui/helpers/country-search';
import styles from './ContactForm.module.scss';
interface ContactFormProps {
  languageCode: LanguageCode;
}
interface FormSchema {
  name: string;
  email: string;
  phone: string;
  country: string;
  companyOrTeam: boolean;
  company?: string;
  teamSize?: string;
  language: string;
  helpQuery?: string | null;
}
const HELP_QUERY_MAX = 1000;
const ContactForm: FC<ContactFormProps> = ({
  languageCode
}) => {
  const locale = useLocale();
  return <ContactFormContent key={locale} languageCode={languageCode} data-sentry-element="ContactFormContent" data-sentry-component="ContactForm" data-sentry-source-file="ContactForm.tsx" />;
};
const ContactFormContent: FC<ContactFormProps> = ({
  languageCode
}) => {
  const t = useTranslations();
  const isRtl = languageCode === 'ar';
  const locale = useLocale();
  const teamSizeOptions = useMemo<SelectMenuOption[]>(() => [{
    value: '1-9',
    label: t('contact.form.team-1-9')
  }, {
    value: '10-99',
    label: t('contact.form.team-10-99')
  }, {
    value: '100-500',
    label: t('contact.form.team-100-500')
  }, {
    value: '500+',
    label: t('contact.form.team-500plus')
  }], [t]);
  const languageOptions = useMemo<SelectMenuOption[]>(() => (['en', 'ar', 'es', 'fr', 'de'] as const).map(code => ({
    value: code,
    label: t(`contact.form.lang-${code}`)
  })), [t]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [schedulingUrl, setSchedulingUrl] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!schedulingUrl || !successRef.current) return;
    const headerRaw = getComputedStyle(document.documentElement).getPropertyValue('--fixed-header-height').trim();
    const headerHeight = Number.parseFloat(headerRaw) || 94;
    const top = successRef.current.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth'
    });
  }, [schedulingUrl]);
  const schema = useMemo(() => yup.object().shape({
    name: yup.string().required(t('contact.form.name-required')).min(2, t('contact.form.name-min')),
    email: yup.string().required(t('contact.form.email-required')).max(100, t('contact.form.email-invalid')).matches(commonConstant.emailRegex, t('contact.form.email-invalid')),
    phone: yup.string().required(t('contact.form.phone-required')).test('valid-phone', t('contact.form.phone-invalid'), value => !!value && isValidPhoneNumber(value)),
    country: yup.string().required(t('contact.form.country-required')),
    companyOrTeam: yup.boolean().default(false),
    company: yup.string().when('companyOrTeam', {
      is: true,
      then: s => s.required(t('contact.form.company-required')).min(2, t('contact.form.company-min')),
      otherwise: s => s.nullable().notRequired()
    }),
    teamSize: yup.string().when('companyOrTeam', {
      is: true,
      then: s => s.required(t('contact.form.teamSize-required')),
      otherwise: s => s.nullable().notRequired()
    }),
    language: yup.string().required(t('contact.form.language-required')),
    helpQuery: yup.string().nullable().max(HELP_QUERY_MAX, t('contact.form.help-error'))
  }), [t]);
  const {
    register,
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    formState: {
      errors
    }
  } = useForm<FormSchema>({
    resolver: yupResolver(schema),
    defaultValues: {
      companyOrTeam: false,
      language: '',
      helpQuery: ''
    }
  });
  const companyOrTeamValue = watch('companyOrTeam');
  const helpQueryValue = watch('helpQuery') || '';
  const onSubmit = async (data: FormSchema) => {
    setIsSubmitting(true);
    setServerError(null);
    try {
      const payload = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        country: data.country,
        language: data.language,
        company: data.companyOrTeam ? data.company || '' : '',
        teamSize: data.companyOrTeam ? data.teamSize || '' : '',
        helpQuery: data.helpQuery || '',
        companyOrTeam: !!data.companyOrTeam
      };
      const res = await fetch('/api/contact-calendly', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || t('contact.form.error'));
      setSchedulingUrl(json.schedulingUrl);
      // window.location.href = json.schedulingUrl;
    } catch (err: any) {
      setServerError(err.message || t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (schedulingUrl) {
    return <div ref={successRef} className={styles.wrapper} dir="ltr">
                <div className={styles.card}>
                    <div className={styles.left}>
                        <div className={styles.mascot}>
                            <Image src="/assets/icons/long-term.svg" alt="Hotdesk" width={105} height={129} priority />
                        </div>
                        <div className={styles.leftContent} dir={isRtl ? 'rtl' : 'ltr'}>
                            <h2 className={styles.title}>
                                {t.rich('skeletonTitle', {
                strong: chunks => <strong>{chunks}</strong>,
                excl: chunks => <span className={styles.titlePunct}>{chunks}</span>
              })}
                            </h2>
                            <p className={styles.desc}>
                                {t('skeletonDescription')}
                            </p>
                        </div>
                    </div>
                    <div className={styles.right}>
                        <div className={styles.successState}>
                            <h3 className={styles.successTitle}>
                                {t('contact.form.success-title')}
                            </h3>
                            <p className={styles.successDesc}>
                                {t('contact.form.success-desc')}
                            </p>
                            <a href={schedulingUrl} className={styles.scheduleBtn} target="_blank" rel="noreferrer">
                                {t('contact.form.schedule-btn')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>;
  }
  return <div className={styles.wrapper} dir="ltr" data-sentry-component="ContactFormContent" data-sentry-source-file="ContactForm.tsx">
            <div className={styles.card}>
                {/* Left panel */}
                <div className={styles.left}>
                    <div className={styles.mascot}>
                        <Image src="/assets/icons/long-term.svg" alt="Hotdesk" width={105} height={129} priority data-sentry-element="Image" data-sentry-source-file="ContactForm.tsx" />
                    </div>
                    <div className={styles.leftContent} dir={isRtl ? 'rtl' : 'ltr'}>
                        <h2 className={styles.title}>
                            {t.rich('skeletonTitle', {
              strong: chunks => <strong>{chunks}</strong>,
              excl: chunks => <span className={styles.titlePunct}>{chunks}</span>
            })}
                        </h2>
                        <p className={styles.desc}>{t('skeletonDescription')}</p>
                    </div>
                </div>

                {/* Right panel — form */}
                <div className={styles.right}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>
                        {/* Name */}
                        <div className={styles.field}>
                            <label className={styles.label}>
                                {t('skeletonName')}
                                <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.inputWrap}>
                                <User size={16} className={styles.icon} data-sentry-element="User" data-sentry-source-file="ContactForm.tsx" />
                                <input {...register('name')} type="text" placeholder={t('skeletonName')} className={`${styles.input} ${errors.name ? styles.inputError : ''}`} />
                            </div>
                            {errors.name && <span className={styles.errorMsg}>
                                    {errors.name.message}
                                </span>}
                        </div>

                        {/* Email */}
                        <div className={styles.field}>
                            <label className={styles.label}>
                                {t('skeletonEmail')}
                                <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.inputWrap}>
                                <Mail size={16} className={styles.icon} data-sentry-element="Mail" data-sentry-source-file="ContactForm.tsx" />
                                <input {...register('email', {
                onBlur: () => {
                  void trigger('email');
                }
              })} type="email" placeholder={t('skeletonEmail')} className={`${styles.input} ${errors.email ? styles.inputError : ''}`} />
                            </div>
                            {errors.email && <span className={styles.errorMsg}>
                                    {errors.email.message}
                                </span>}
                        </div>

                        {/* Phone */}
                        <div className={styles.field}>
                            <label className={styles.label}>
                                {t('skeletonPhone')}
                                <span className={styles.required}>*</span>
                            </label>
                            <div className={`${styles.phoneWrap} ${errors.phone ? styles.inputError : ''}`}>
                                <Controller name="phone" control={control} render={({
                field
              }) => <AppPhoneInput value={field.value || undefined} onChange={v => field.onChange(v as string ?? '')} onBlur={() => {
                field.onBlur();
                void trigger('phone');
              }} className={styles.phoneInput} placeholder={t('skeletonPhone')} />} data-sentry-element="Controller" data-sentry-source-file="ContactForm.tsx" />
                            </div>
                            {errors.phone && <span className={styles.errorMsg}>
                                    {errors.phone.message}
                                </span>}
                        </div>

                        {/* Country */}
                        <div className={styles.field}>
                            <label id="contact-country" className={styles.label}>
                                {t('skeletonCountry')}
                                <span className={styles.required}>*</span>
                            </label>
                            <Controller name="country" control={control} render={({
              field
            }) => <SearchSelect value={field.value || ''} onChange={next => setValue('country', next, {
              shouldValidate: true
            })} onBlur={field.onBlur} search={q => searchCountries(q, locale)}
            // Every country on open, A–Z; typing filters.
            minChars={0} maxVisible={Infinity} maxMenuHeight={264} invalid={!!errors.country} placeholder={t('contact.form.country-placeholder')} hintText={t('contact.form.country-search-hint')} loadingText={t('contact.form.country-searching')} noResultsText={t('contact.form.country-no-results')} otherLabel={t('contact.form.country-other')} aria-labelledby="contact-country" inputClassName={styles.input} leadingIcon={<Globe size={16} />} />} data-sentry-element="Controller" data-sentry-source-file="ContactForm.tsx" />
                            {errors.country && <span className={styles.errorMsg}>
                                    {errors.country.message}
                                </span>}
                        </div>

                        {/* Company / team checkbox + accordion fields */}
                        <div className={styles.companyBlock}>
                            <Controller name="companyOrTeam" control={control} render={({
              field
            }) => <div className={styles.companyOrTeam}>
                                        <button type="button" role="checkbox" aria-checked={!!field.value} aria-controls="contact-company-fields" className={`${styles.checkbox} ${field.value ? styles.checkboxChecked : ''}`} onClick={() => field.onChange(!field.value)}>
                                            <Check size={12} strokeWidth={4} />
                                        </button>
                                        <div>
                                            <h3 className={styles.companyOrTeamTitle}>
                                                {t('contact.form.companyOrTeamTitle')}
                                            </h3>
                                            <p className={styles.companyOrTeamDesc}>
                                                {t('contact.form.companyOrTeamDescription')}
                                            </p>
                                        </div>
                                    </div>} data-sentry-element="Controller" data-sentry-source-file="ContactForm.tsx" />

                            <div id="contact-company-fields" className={`${styles.companyFields} ${companyOrTeamValue ? styles.companyFieldsOpen : ''}`} aria-hidden={!companyOrTeamValue}>
                                <div className={styles.companyFieldsClip}>
                                    <div className={styles.companyFieldsInner}>
                                        {/* Company Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('skeletonCompany')}
                                                <span className={styles.required}>
                                                    *
                                                </span>
                                            </label>
                                            <div className={styles.inputWrap}>
                                                <Building2 size={16} className={styles.icon} data-sentry-element="Building2" data-sentry-source-file="ContactForm.tsx" />
                                                <input {...register('company')} type="text" placeholder={t('contact.form.company-placeholder')} tabIndex={companyOrTeamValue ? 0 : -1} className={`${styles.input} ${errors.company ? styles.inputError : ''}`} />
                                            </div>
                                            {errors.company && <span className={styles.errorMsg}>
                                                    {errors.company.message}
                                                </span>}
                                        </div>

                                        {/* Team size */}
                                        <div className={styles.field}>
                                            <label id="contact-team-size" className={styles.label}>
                                                {t('skeletonTeamSize')}
                                                <span className={styles.required}>
                                                    *
                                                </span>
                                            </label>
                                            <Controller name="teamSize" control={control} render={({
                      field
                    }) => <SelectMenu name={field.name} value={field.value || ''} onChange={next => setValue('teamSize', next, {
                      shouldValidate: true
                    })} onBlur={field.onBlur} disabled={!companyOrTeamValue} invalid={!!errors.teamSize} placeholder={t('contact.form.team-placeholder')} aria-labelledby="contact-team-size" triggerClassName={styles.menuTrigger} menuClassName={styles.menu} leadingIcon={<Users size={16} />} options={teamSizeOptions} />} data-sentry-element="Controller" data-sentry-source-file="ContactForm.tsx" />
                                            {errors.teamSize && <span className={styles.errorMsg}>
                                                    {errors.teamSize.message}
                                                </span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preferred language */}
                        <div className={styles.field}>
                            <label id="contact-language" className={styles.label}>
                                {t('skeletonLanguage')}
                                <span className={styles.required}>*</span>
                            </label>
                            <Controller name="language" control={control} render={({
              field
            }) => <SelectMenu name={field.name} value={field.value || ''} onChange={next => setValue('language', next, {
              shouldValidate: true
            })} onBlur={field.onBlur} invalid={!!errors.language} placeholder={t('contact.form.language-placeholder')} aria-labelledby="contact-language" triggerClassName={styles.menuTrigger} menuClassName={styles.menu} leadingIcon={<Globe size={16} />} options={languageOptions} />} data-sentry-element="Controller" data-sentry-source-file="ContactForm.tsx" />
                            {errors.language && <span className={styles.errorMsg}>
                                    {errors.language.message}
                                </span>}
                        </div>

                        {/* How can we help? */}
                        <div className={styles.field}>
                            <label className={styles.label}>
                                {t('skeletonHelp')}
                            </label>
                            <textarea {...register('helpQuery')} className={`${styles.textarea} ${errors.helpQuery ? styles.inputError : ''}`} placeholder={t('contact.form.help-placeholder')} rows={4} maxLength={HELP_QUERY_MAX} />
                            <p className={styles.charCount}>
                                {helpQueryValue.length}/{HELP_QUERY_MAX}
                            </p>
                            {errors.helpQuery && <span className={styles.errorMsg}>
                                    {errors.helpQuery.message}
                                </span>}
                        </div>

                        {serverError && <p className={styles.serverError}>{serverError}</p>}

                        <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
                            {isSubmitting ? '…' : t('skeletonSubmit')}
                        </button>
                    </form>
                </div>
            </div>
        </div>;
};
export default ContactForm;